import { observable, action, computed } from "mobx";
import { dateFormatForFrameworkChanges } from "../constants/constants-container";
import toastService from "src/services/ToastService";
import moment from "moment";

moment.locale("en");

export default class FrameworkComparisonStore {
  constructor(exporter) {
    this.exporter = exporter;
  }

  @observable selectedSnapshotOne = {
    tid: null,
    name: null,
    date: null,
  };

  @observable selectedSnapshotTwo = {
    tid: null,
    name: null,
    date: null,
  };

  @action
  selectSnapshotOne = (snapshotValue) => {
    // react-select clear feature sends null value
    if (snapshotValue?.value) {
      const snapshotPropertiesArray = snapshotValue.value.split(",");
      this.selectedSnapshotOne = {
        tid: snapshotPropertiesArray[0],
        name: snapshotPropertiesArray[1],
        date: ["undefined", "null"].includes(snapshotPropertiesArray[2]) ? null : snapshotPropertiesArray[2],
      };
    } else {
      this.selectedSnapshotOne = {
        tid: null,
        name: null,
        date: null,
      };
    }
  };

  @action
  selectSnapshotTwo = (snapshotValue) => {
    // react-select clear feature sends null value
    if (snapshotValue?.value) {
      const snapshotPropertiesArray = snapshotValue.value.split(",");
      this.selectedSnapshotTwo = {
        tid: snapshotPropertiesArray[0],
        name: snapshotPropertiesArray[1],
        date: ["undefined", "null"].includes(snapshotPropertiesArray[2]) ? null : snapshotPropertiesArray[2],
      };
    } else {
      this.selectedSnapshotTwo = {
        tid: null,
        name: null,
        date: null,
      };
    }
  };

  @action
  clearBothSnapshot = () => {
    this.selectSnapshotOne(null)
    this.selectSnapshotTwo(null)
  }

  @action
  compareFrameworks = (frameworks) => {
    if (!this.areBothSnapshotFilled() || this.areBothSnapshotsEqual()) {
      toastService.func_notify("Please select different releases", "error")
      return;
    }
    
    const comaSeperatedFrameworkIds = frameworks ? frameworks.map(f => f.frameworkId).join(",") : "";
    this.exporter.compareFrameworks(
      this.selectedSnapshotOne,
      this.selectedSnapshotTwo,
      comaSeperatedFrameworkIds
    );
  };

  areBothSnapshotsEqual = () => {
    return JSON.stringify(this.selectedSnapshotOne) === JSON.stringify(this.selectedSnapshotTwo)
  }

  areBothSnapshotFilled = () => {
    return this.isSnapshotFilled(this.selectedSnapshotOne) && this.isSnapshotFilled(this.selectedSnapshotTwo)
  }

  isSnapshotFilled = ({ tid, name }) => {
    return tid && name
  }

  isSnapshotNotSelectedYet = (snapshot) => {
    const snapshotOneTidAsNumber =
      this.selectedSnapshotOne.tid !== null
        ? Number(this.selectedSnapshotOne.tid)
        : null;
    const snapshotTwoTidAsNumber =
      this.selectedSnapshotTwo.tid !== null
        ? Number(this.selectedSnapshotTwo.tid)
        : null;
    if (snapshot.tid !== 1) {
      return (
        snapshot.tid !== snapshotOneTidAsNumber &&
        snapshot.tid !== snapshotTwoTidAsNumber
      );
    } else {
      return (
        !snapshot.name.includes(this.selectedSnapshotOne.name) &&
        !snapshot.name.includes(this.selectedSnapshotTwo.name)
      );
    }
  };

  getSelectableSnapshots = (selectedProject) => {
    return computed(() => {
      let selectableSnapshots;
      if (selectedProject.intermediateReleases) {
        selectableSnapshots = selectedProject.intermediateReleases
          .filter((ir) => ir.status === "ACTIVE")
          .filter((ir) => this.isSnapshotNotSelectedYet(ir))
          .map((ir) => ({
            label: ir.name,
            value: ir.tid + "," + ir.name + "," + ir.creationDate,
            releaseDate: ir.creationDate
          }));
      } else {
        selectableSnapshots = [];
      }

      if (
        selectedProject.finalRelease &&
        selectedProject.finalRelease.status &&
        selectedProject.finalRelease.status === "ACTIVE" &&
        this.isSnapshotNotSelectedYet(selectedProject.finalRelease)
      ) {
        const { tid, name, creationDate } = selectedProject.finalRelease;

        selectableSnapshots.push({
          label: name,
          value: tid + "," + name + "," + creationDate,
          releaseDate: creationDate
        });
      }

      let earliestRelease =
        selectedProject.intermediateReleases &&
        selectedProject.intermediateReleases.length > 0
          ? selectedProject.intermediateReleases[0]
          : null;

      if (selectedProject.intermediateReleases) {
        for (let i = 1; i < selectedProject.intermediateReleases.length; i++) {
          if (
            moment(
              selectedProject.intermediateReleases[i].creationDate,
              "YYYY-MM-DD"
            ).isBefore(moment(earliestRelease.creationDate, "YYYY-MM-DD"))
          ) {
            earliestRelease = selectedProject.intermediateReleases[i];
          }
        }
      }

      if (
        selectedProject.finalRelease &&
        selectedProject.finalRelease.creationDate
      ) {
        if (
          earliestRelease === null ||
          moment(
            selectedProject.finalRelease.creationDate,
            "YYYY-MM-DD"
          ).isBefore(moment(earliestRelease.creationDate, "YYYY-MM-DD"))
        ) {
          earliestRelease = selectedProject.finalRelease;
        }
      }

      const initialProductionSnapshot = {
        tid: 1,
        name: "Initial Production",
        date:
          earliestRelease !== null &&
          moment(selectedProject.project.creationDate).isSame(
            moment(earliestRelease.creationDate)
          )
            ? moment(earliestRelease.creationDate)
                .subtract(1, "days")
                .endOf("day")
                .format(dateFormatForFrameworkChanges)
            : moment(selectedProject.project.creationDate)
                .add(1, "days")
                .startOf("day")
                .format(dateFormatForFrameworkChanges),
      };

      const currentProductionSnapshot = {
        tid: 1,
        name: "Current Production",
        date: null,
      };

      if (this.isSnapshotNotSelectedYet(initialProductionSnapshot)) {
        selectableSnapshots.push({
          label:
            initialProductionSnapshot.name,
          value:
            initialProductionSnapshot.tid +
            "," +
            initialProductionSnapshot.name +
            "," +
            initialProductionSnapshot.date,
          releaseDate: initialProductionSnapshot.date
        });
      }
      if (this.isSnapshotNotSelectedYet(currentProductionSnapshot)) {
        selectableSnapshots.push({
          label: currentProductionSnapshot.name,
          value:
            currentProductionSnapshot.tid +
            "," +
            currentProductionSnapshot.name +
            "," +
            currentProductionSnapshot.date,
          releaseDate: currentProductionSnapshot.date
        });
      }
      return selectableSnapshots;
    }).get();
  };

  @computed
  get getSelectedSnapshotOne() {
    return this.selectedSnapshotOne.tid !== null
      ? {
          label: this.selectedSnapshotOne.name,
          value:
            this.selectedSnapshotOne.tid +
            "," +
            this.selectedSnapshotOne.name +
            "," +
            this.selectedSnapshotOne.date,
        }
      : null;
  }

  @computed
  get getSelectedSnapshotTwo() {
    return this.selectedSnapshotTwo.tid !== null
      ? {
          label: this.selectedSnapshotTwo.name,
          value:
            this.selectedSnapshotTwo.tid +
            "," +
            this.selectedSnapshotTwo.name +
            "," +
            this.selectedSnapshotTwo.date,
        }
      : null;
  }
}
